import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Dropdown, Modal, Form } from 'react-bootstrap';
import { getFirestore, collection, addDoc, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { QRCodeSVG } from 'qrcode.react';

const StudentAttendance = () => {
  // State initialization
  const [attendance, setAttendance] = useState([]);
  const [form, setForm] = useState({ 
    name: '', 
    class: '', 
    rollNumber: '',
    status: 'Present', 
    inTime: '', 
    outTime: '',
    date: new Date().toISOString().slice(0, 10)
  });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ name: '', class: '', date: '', status: '', rollNumber: '' });
  const [exportPeriod, setExportPeriod] = useState('day');
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentQRData, setCurrentQRData] = useState('');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [highlight, setHighlight] = useState(-1);
  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showBulkQRModal, setShowBulkQRModal] = useState(false);
  const [rollNumberError, setRollNumberError] = useState('');

  // Fetch attendance data from Firestore
  useEffect(() => {
    const db = getFirestore();
    const q = query(collection(db, 'attendance'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const attendanceData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        name: doc.data().name || '',
        class: doc.data().class || '',
        rollNumber: doc.data().rollNumber || '',
        status: doc.data().status || 'Present',
        inTime: doc.data().inTime || '',
        outTime: doc.data().outTime || '',
        date: doc.data().date || new Date().toISOString().slice(0, 10)
      }));
      setAttendance(attendanceData);
    });
    return () => unsub();
  }, []);

  // Check if roll number already exists
  const checkRollNumberExists = async (rollNumber, excludeId = null) => {
    const db = getFirestore();
    const q = query(
      collection(db, 'attendance'), 
      where('rollNumber', '==', rollNumber)
    );
    
    // For simplicity, we'll check locally since we have all data
    const existingRecord = attendance.find(record => 
      record.rollNumber === rollNumber && record.id !== excludeId
    );
    
    return !!existingRecord;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRollNumberError('');
    
    // Validate required fields
    if (!form.name.trim() || !form.class.trim() || !form.rollNumber || !form.inTime || !form.outTime) {
      alert('Please fill all required fields');
      setLoading(false);
      return;
    }

    // Check if roll number already exists (only for new records)
    if (!editMode) {
      const rollNumberExists = await checkRollNumberExists(form.rollNumber);
      if (rollNumberExists) {
        setRollNumberError('This roll number already exists. Please use a unique roll number.');
        setLoading(false);
        return;
      }
    }

    // For edit mode, check if roll number exists for other records
    if (editMode) {
      const rollNumberExists = await checkRollNumberExists(form.rollNumber, currentEditId);
      if (rollNumberExists) {
        setRollNumberError('This roll number already exists for another student. Please use a unique roll number.');
        setLoading(false);
        return;
      }
    }

    try {
      const db = getFirestore();
      const attendanceData = {
        name: form.name.trim(),
        class: form.class.trim(),
        rollNumber: form.rollNumber,
        status: form.status,
        inTime: form.inTime,
        outTime: form.outTime,
        date: form.date,
        timestamp: Date.now()
      };

      if (editMode) {
        await updateDoc(doc(db, 'attendance', currentEditId), attendanceData);
        setEditMode(false);
        setCurrentEditId(null);
      } else {
        await addDoc(collection(db, 'attendance'), attendanceData);
      }
      
      // Reset form
      setForm({ 
        name: '', 
        class: '', 
        rollNumber: '',
        status: 'Present', 
        inTime: '', 
        outTime: '',
        date: new Date().toISOString().slice(0, 10)
      });
    } catch (err) {
      console.error('Error saving attendance:', err);
      alert('Error saving attendance. Please check console for details.');
    }
    setLoading(false);
  };

  // Handle edit
  const handleEdit = (record) => {
    setForm({
      name: record.name,
      class: record.class,
      rollNumber: record.rollNumber,
      status: record.status,
      inTime: record.inTime,
      outTime: record.outTime,
      date: record.date
    });
    setEditMode(true);
    setCurrentEditId(record.id);
    setRollNumberError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle delete confirmation
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'attendance', deleteId));
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting record:', err);
      alert('Error deleting record. Please check console for details.');
    }
  };

  // Highlight rows animation
  useEffect(() => {
    if (attendance.length > 0) {
      let i = 0;
      const interval = setInterval(() => {
        setHighlight(i);
        i = (i + 1) % attendance.length;
      }, 1200);
      return () => clearInterval(interval);
    }
  }, [attendance]);

  // Filter attendance based on user selection
  const filteredAttendance = attendance.filter(row =>
    row.name.toLowerCase().includes(filters.name.toLowerCase()) &&
    row.class.toLowerCase().includes(filters.class.toLowerCase()) &&
    row.rollNumber.toLowerCase().includes(filters.rollNumber.toLowerCase()) &&
    (filters.date ? row.date === filters.date : true) &&
    (filters.status ? row.status === filters.status : true)
  );

  // Generate PDF report
  const generatePDF = () => {
    const doc = new jsPDF();
    const title = `Attendance Report - ${getPeriodTitle()}`;
    
    doc.setFontSize(18);
    doc.text(title, 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);
    
    const tableData = getFilteredDataForExport().map(row => [
      row.rollNumber,
      row.name,
      row.class,
      row.date,
      row.status,
      row.inTime || '--',
      row.outTime || '--'
    ]);
    
    autoTable(doc, {
      head: [['Roll No', 'Name', 'Class', 'Date', 'Status', 'In Time', 'Out Time']],
      body: tableData,
      startY: 30,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 20 },
        3: { cellWidth: 30 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 }
      }
    });
    
    doc.save(`attendance_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Get filtered data based on export period
  const getFilteredDataForExport = () => {
    const today = new Date();
    const currentDate = today.toISOString().slice(0, 10);
    
    switch(exportPeriod) {
      case 'day':
        return filteredAttendance.filter(row => 
          filters.date ? row.date === filters.date : row.date === currentDate
        );
      case 'week':
        const oneWeekAgo = new Date(today.setDate(today.getDate() - 7)).toISOString().slice(0, 10);
        return filteredAttendance.filter(row => row.date >= oneWeekAgo && row.date <= currentDate);
      case 'month':
        const oneMonthAgo = new Date(today.setMonth(today.getMonth() - 1)).toISOString().slice(0, 10);
        return filteredAttendance.filter(row => row.date >= oneMonthAgo && row.date <= currentDate);
      case 'custom':
        return filteredAttendance.filter(row => 
          row.date >= customRange.start && row.date <= customRange.end
        );
      default:
        return filteredAttendance;
    }
  };

  // Get title for PDF based on selected period
  const getPeriodTitle = () => {
    const today = new Date();
    switch(exportPeriod) {
      case 'day':
        return filters.date || today.toISOString().slice(0, 10);
      case 'week':
        const oneWeekAgo = new Date(today.setDate(today.getDate() - 7)).toISOString().slice(0, 10);
        return `${oneWeekAgo} to ${today.toISOString().slice(0, 10)}`;
      case 'month':
        const oneMonthAgo = new Date(today.setMonth(today.getMonth() - 1)).toISOString().slice(0, 10);
        return `${oneMonthAgo} to ${today.toISOString().slice(0, 10)}`;
      case 'custom':
        return `${customRange.start} to ${customRange.end}`;
      default:
        return 'All Data';
    }
  };

  // Generate QR code data
  const generateQRCode = (row) => {
    const qrData = JSON.stringify({
      rollNumber: row.rollNumber,
      name: row.name,
      class: row.class,
      date: row.date,
      status: row.status,
      inTime: row.inTime || '--',
      outTime: row.outTime || '--'
    });
    setCurrentQRData(qrData);
    setShowQRModal(true);
  };

  // Render QR code content in modal
  const renderQRContent = () => {
    try {
      const data = JSON.parse(currentQRData);
      return (
        <div style={{ textAlign: 'left', marginTop: 20 }}>
          <p><strong>Roll No:</strong> {data.rollNumber}</p>
          <p><strong>Name:</strong> {data.name}</p>
          <p><strong>Class:</strong> {data.class}</p>
          <p><strong>Date:</strong> {data.date}</p>
          <p><strong>Status:</strong> {data.status}</p>
          <p><strong>Time:</strong> {data.inTime} - {data.outTime}</p>
        </div>
      );
    } catch {
      return <div style={{ marginTop: 20 }}>{currentQRData}</div>;
    }
  };

  // Generate QR data for bulk printing
  const generateBulkQRData = (row) => {
    return JSON.stringify({
      rollNumber: row.rollNumber,
      name: row.name,
      class: row.class,
      date: row.date,
      status: row.status,
      inTime: row.inTime || '--',
      outTime: row.outTime || '--'
    });
  };

  // Handle bulk QR print
  const handleBulkQRPrint = () => {
    setShowBulkQRModal(true);
  };

  // Print all QR codes
  const printAllQRCodes = () => {
    window.print();
  };

  // Handle roll number change with validation
  const handleRollNumberChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, rollNumber: value });
    
    // Clear error when user starts typing
    if (rollNumberError) {
      setRollNumberError('');
    }
  };

  return (
    <div style={{padding: 0}}>
      {/* Header with export controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 700, margin: 0 }}>Student Attendance (Real-Time, DB)</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button 
            onClick={handleBulkQRPrint} 
            variant="info" 
            style={{ borderRadius: 8, fontWeight: 600 }}
            disabled={filteredAttendance.length === 0}
          >
            Bulk QR Print
          </Button>
          
          <Dropdown>
            <Dropdown.Toggle variant="primary" style={{ borderRadius: 8, fontWeight: 600 }}>
              Export: {exportPeriod.charAt(0).toUpperCase() + exportPeriod.slice(1)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setExportPeriod('day')}>Daily</Dropdown.Item>
              <Dropdown.Item onClick={() => setExportPeriod('week')}>Weekly</Dropdown.Item>
              <Dropdown.Item onClick={() => setExportPeriod('month')}>Monthly</Dropdown.Item>
              <Dropdown.Item onClick={() => setExportPeriod('custom')}>Custom Range</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          
          {exportPeriod === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input 
                type="date" 
                value={customRange.start} 
                onChange={e => setCustomRange({...customRange, start: e.target.value})} 
                style={{ borderRadius: 8, border: '1px solid #ccc', padding: 6 }}
              />
              <span>to</span>
              <input 
                type="date" 
                value={customRange.end} 
                onChange={e => setCustomRange({...customRange, end: e.target.value})} 
                style={{ borderRadius: 8, border: '1px solid #ccc', padding: 6 }}
              />
            </div>
          )}
          
          <Button 
            onClick={generatePDF} 
            variant="success" 
            style={{ borderRadius: 8, fontWeight: 600 }}
            disabled={
              filteredAttendance.length === 0 || 
              (exportPeriod === 'custom' && (!customRange.start || !customRange.end))
            }
          >
            Download PDF
          </Button>
        </div>
      </div>

      {/* Main card with form and table */}
      <Card style={{ borderRadius: 16, boxShadow: '0 4px 24px rgba(25,118,210,0.10)', marginBottom: 24 }}>
        <Card.Body>
          {/* Attendance form */}
          <Form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Form.Group style={{ minWidth: 200 }}>
                <Form.Label>Roll Number*</Form.Label>
                <Form.Control 
                  type="text" 
                  required 
                  value={form.rollNumber} 
                  onChange={handleRollNumberChange} 
                  placeholder="Roll Number"
                  isInvalid={!!rollNumberError}
                />
                <Form.Control.Feedback type="invalid">
                  {rollNumberError}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Roll number must be unique for each student
                </Form.Text>
              </Form.Group>
              
              <Form.Group style={{ minWidth: 200 }}>
                <Form.Label>Name*</Form.Label>
                <Form.Control 
                  type="text" 
                  required 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  placeholder="Student Name" 
                />
              </Form.Group>
              
              <Form.Group style={{ minWidth: 150 }}>
                <Form.Label>Class*</Form.Label>
                <Form.Control 
                  type="text" 
                  required 
                  value={form.class} 
                  onChange={e => setForm({...form, class: e.target.value})} 
                  placeholder="Class" 
                />
              </Form.Group>
              
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>Date*</Form.Label>
                <Form.Control 
                  type="date" 
                  required 
                  value={form.date} 
                  onChange={e => setForm({...form, date: e.target.value})} 
                />
              </Form.Group>
              
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>In Time*</Form.Label>
                <Form.Control 
                  type="time" 
                  required 
                  value={form.inTime} 
                  onChange={e => setForm({...form, inTime: e.target.value})} 
                />
              </Form.Group>
              
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>Out Time*</Form.Label>
                <Form.Control 
                  type="time" 
                  required 
                  value={form.outTime} 
                  onChange={e => setForm({...form, outTime: e.target.value})} 
                />
              </Form.Group>
              
              <Form.Group style={{ minWidth: 150 }}>
                <Form.Label>Status</Form.Label>
                <Form.Select 
                  value={form.status} 
                  onChange={e => setForm({...form, status: e.target.value})}
                >
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="Late">Late</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            <div style={{ marginTop: 16 }}>
              <Button 
                type="submit" 
                disabled={loading || !!rollNumberError} 
                style={{ borderRadius: 8, fontWeight: 600, marginRight: 10 }}
                variant={editMode ? "warning" : "primary"}
              >
                {loading ? 'Saving...' : editMode ? 'Update Record' : 'Add Attendance'}
              </Button>
              
              {editMode && (
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setEditMode(false);
                    setCurrentEditId(null);
                    setRollNumberError('');
                    setForm({ 
                      name: '', 
                      class: '', 
                      rollNumber: '',
                      status: 'Present', 
                      inTime: '', 
                      outTime: '',
                      date: new Date().toISOString().slice(0, 10)
                    });
                  }}
                  style={{ borderRadius: 8, fontWeight: 600 }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </Form>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Form.Control 
              value={filters.rollNumber} 
              onChange={e => setFilters({...filters, rollNumber: e.target.value})} 
              placeholder="Filter Roll No" 
              style={{ borderRadius: 8, minWidth: 120 }} 
            />
            <Form.Control 
              value={filters.name} 
              onChange={e => setFilters({...filters, name: e.target.value})} 
              placeholder="Filter Name" 
              style={{ borderRadius: 8, minWidth: 120 }} 
            />
            <Form.Control 
              value={filters.class} 
              onChange={e => setFilters({...filters, class: e.target.value})} 
              placeholder="Filter Class" 
              style={{ borderRadius: 8, minWidth: 100 }} 
            />
            <Form.Control 
              type="date" 
              value={filters.date} 
              onChange={e => setFilters({...filters, date: e.target.value})} 
              style={{ borderRadius: 8 }} 
            />
            <Form.Select 
              value={filters.status} 
              onChange={e => setFilters({...filters, status: e.target.value})} 
              style={{ borderRadius: 8, minWidth: 120 }}
            >
              <option value="">All Status</option>
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Late">Late</option>
            </Form.Select>
          </div>

          {/* Attendance table */}
          <Table responsive hover size="sm" style={{ background: '#fff', borderRadius: 10, fontSize: 16 }}>
            <thead>
              <tr style={{ background: '#f5f7fa' }}>
                <th>Roll No</th>
                <th>Name</th>
                <th>Class</th>
                <th>Date</th>
                <th>Status</th>
                <th>Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttendance.length > 0 ? (
                filteredAttendance.map((row, i) => (
                  <tr 
                    key={row.id} 
                    style={{ 
                      background: highlight === i ? '#e3f2fd' : 'none', 
                      transition: 'background 0.5s' 
                    }}
                  >
                    <td style={{ fontWeight: 600 }}>{row.rollNumber}</td>
                    <td style={{ fontWeight: 600 }}>{row.name}</td>
                    <td>{row.class}</td>
                    <td>{row.date}</td>
                    <td>
                      <span style={{
                        color: row.status === 'Present' ? '#43a047' : 
                              row.status === 'Absent' ? '#d32f2f' : '#fbc02d',
                        fontWeight: 700,
                        letterSpacing: 1,
                        animation: highlight === i ? 'pulse 1s infinite alternate' : 'none',
                      }}>
                        {row.status}
                      </span>
                    </td>
                    <td>{row.inTime || '--'} - {row.outTime || '--'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => generateQRCode(row)}
                          style={{ padding: '2px 8px', fontSize: 12 }}
                        >
                          QR
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          onClick={() => handleEdit(row)}
                          style={{ padding: '2px 8px', fontSize: 12 }}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={() => handleDelete(row.id)}
                          style={{ padding: '2px 8px', fontSize: 12 }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    No attendance records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* QR Code Modal */}
      <Modal show={showQRModal} onHide={() => setShowQRModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Attendance QR Code</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <QRCodeSVG 
            value={currentQRData} 
            size={200} 
            level="H" 
            includeMargin={true}
          />
          {renderQRContent()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQRModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk QR Print Modal */}
      <Modal 
        show={showBulkQRModal} 
        onHide={() => setShowBulkQRModal(false)} 
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Bulk QR Codes - Ready to Print</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="print-only" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3>Student QR Codes</h3>
            <p>Generated on: {new Date().toLocaleDateString()}</p>
          </div>
          
          <div className="qr-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '20px',
            padding: '10px'
          }}>
            {filteredAttendance.map((row, index) => (
              <div 
                key={row.id} 
                className="qr-item" 
                style={{ 
                  textAlign: 'center', 
                  padding: '15px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  pageBreakInside: 'avoid'
                }}
              >
                <div style={{ marginBottom: '10px' }}>
                  <QRCodeSVG 
                    value={generateBulkQRData(row)} 
                    size={150} 
                    level="H" 
                    includeMargin={true}
                  />
                </div>
                <div 
                  style={{ 
                    fontWeight: 'bold', 
                    fontSize: '16px', 
                    marginTop: '8px',
                    color: '#333'
                  }}
                >
                  {row.rollNumber}
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Total QR Codes: {filteredAttendance.length}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkQRModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={printAllQRCodes}>
            Print All QR Codes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this attendance record? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .modal-content,
          .modal-content * {
            visibility: visible;
          }
          .modal {
            position: absolute;
            left: 0;
            top: 0;
            margin: 0;
            padding: 0;
            opacity: 1;
          }
          .modal-dialog {
            max-width: none;
            width: 100%;
          }
          .modal-header,
          .modal-footer {
            display: none !important;
          }
          .qr-grid {
            display: grid !important;
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 15px !important;
          }
          .qr-item {
            break-inside: avoid;
            border: 1px solid #000 !important;
          }
          .print-only {
            display: block !important;
          }
        }
        
        @keyframes pulse {
          0% { filter: brightness(1); }
          100% { filter: brightness(1.3); }
        }
        
        .print-only {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default StudentAttendance;